// Sous-vue "Statistiques" de la page Usage des vues (BI-693) : GET /api/usage/stats,
// memes parametres que /api/usage/views. Deux mises en page (?layout=) :
// dashboard (defaut) : tuiles + courbes + cartes a en-tete ; explore : arbre radial sombre.
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Card, Col, Empty, Row, Spin, Tooltip } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Area, Heatmap } from "@ant-design/charts";

import axios from "../../utils/axios";
import { ajouterJours, dateValide } from "./periode";
import { formaterDate, formaterDureeMs, formaterNombre, urlReferentiel } from "./usageVuesFormat";

export const NUIT = "#0f2b46";
export const BLEU = "#2a78d6";
export const ORANGE = "#d46b08";
export const VERT = "#1baf7a";
export const JAUNE = "#eda100";
export const ROUGE = "#e34948";
export const GRIS = "#d3d1c7";
export const GRIS_HORS = "#b4b2a9";
export const HORS = "Hors référentiel";
export const CLE_HORS = "__hors";

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const liste = (v) => (Array.isArray(v) ? v : []);
const nb = (v) => Number(v) || 0;
const vide = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune donnée" />;
const CARTE = { borderRadius: 12, border: "1px solid #e6e8ec", boxShadow: "none", height: "100%", overflow: "hidden" };

export const formaterDelta = (d) => (Number(d) > 0 ? `+${formaterNombre(d)}` : formaterNombre(d));

// Evolution en % entre la periode precedente et la periode courante ; null si non calculable.
export const evolution = (courant, precedent) => (nb(precedent) > 0 ? ((nb(courant) - nb(precedent)) / nb(precedent)) * 100 : null);
// "2026-09-03" -> "03/09/2026"
export const formaterJour = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
export const formaterEvolution = (e) => (e === null ? "—" : `${e > 0 ? "+" : ""}${Math.round(e)} %`);
export const couleurEvolution = (e) => (e === null || Math.round(e) === 0 ? "#6b7785" : e > 0 ? VERT : ROUGE);

// Tendance d'une vue : hausse / baisse a partir de +-20 %, sinon stable.
export function tendance(v) {
  const e = evolution(v.opens, v.previous_opens);
  if (e === null) return nb(v.opens) > 0 && nb(v.previous_opens) === 0 ? "up" : "stable";
  return e >= SEUIL_TENDANCE ? "up" : e <= -SEUIL_TENDANCE ? "down" : "stable";
}
const COULEUR_TENDANCE = { up: VERT, down: ROUGE, stable: BLEU };

const pct = (a, b) => (nb(b) > 0 ? (nb(a) / nb(b)) * 100 : 0);
const fmtPct = (v) => `${Math.round(v)} %`;
const vues = (n) => `${formaterNombre(n)} vue${nb(n) > 1 ? "s" : ""}`;
// Tout ce qui n'a pas de libelle de module (vue hors referentiel, code brut du fichier) : un seul groupe.
export const libelleModule = (o) => (o && o.module_label) || HORS;
export const cleModule = (o) => (o && o.module_label ? o.module || "?" : CLE_HORS);
// Un code de vue n'apparait jamais seul : toujours avec son libelle.
export const nomVue = (v) => `${v.code} · ${v.label || "sans libellé"}`;
// Libelles uniques d'une meme notion (tuiles, cartes, fiches, regles).
export const LIB = {
  p95: "95 % des ouvertures en moins de",
  ouvertures: "Ouvertures",
  environnements: "Environnements",
  jamais: "Jamais utilisées",
  precedente: "Période précédente",
};
export const SEUIL_TENDANCE = 20;

/* Carte blanche a en-tete bleu nuit. */
function Bloc({ id, titre, children, extra }) {
  return (
    <Card
      size="small"
      title={titre}
      extra={extra}
      data-testid={id}
      style={{ ...CARTE, height: "100%", display: "flex", flexDirection: "column" }}
      headStyle={{ background: NUIT, color: "#fff", borderBottom: 0, fontSize: 15, fontWeight: 500 }}
      bodyStyle={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      {children}
    </Card>
  );
}

const Rangee = ({ children }) => (
  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
    {children}
  </Row>
);

function Barre({ valeur, max, couleur = BLEU }) {
  const w = max > 0 ? Math.max(2, Math.min(100, (nb(valeur) / max) * 100)) : 0;
  return (
    <div style={{ background: "#eef1f5", borderRadius: 4, height: 8, flex: 1 }}>
      <div style={{ width: `${w}%`, background: couleur, height: 8, borderRadius: 4 }} />
    </div>
  );
}

// Les lignes se repartissent sur la hauteur de la carte (space-between) : pas de vide en bas.
function ListeBarres({ id, lignes }) {
  if (lignes.length === 0) return vide;
  const max = Math.max(...lignes.map((l) => nb(l.valeur)), 0);
  return (
    <div data-testid={id} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, justifyContent: "space-between" }}>
      {lignes.map((l) => (
        <div key={l.cle}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, gap: 8 }}>
            <Infobulle texte={l.titre}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {l.titre}
                {l.puce}
              </span>
            </Infobulle>
            <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{l.texte}</span>
          </div>
          <Barre valeur={l.valeur} max={l.max || max} couleur={l.couleur} />
        </div>
      ))}
    </div>
  );
}

// Infobulle antd (se ferme a la sortie du survol) a la place de l'attribut title natif, qui peut rester affiche.
const Infobulle = ({ texte, children }) =>
  texte ? (
    <Tooltip title={texte} placement="top" mouseLeaveDelay={0.05}>
      {children}
    </Tooltip>
  ) : (
    children
  );

/* ---------- Mini-graphiques SVG ---------- */
// Chemin lisse (milieux de segments + courbes quadratiques) passant au plus pres des points.
export function cheminLisse(pts) {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M${pts[0][0]},${pts[0][1]}`;
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i][0] + pts[i + 1][0]) / 2;
    const my = (pts[i][1] + pts[i + 1][1]) / 2;
    d += ` Q${pts[i][0]},${pts[i][1]} ${mx},${my}`;
  }
  const l = pts[pts.length - 1];
  return `${d} T${l[0]},${l[1]}`;
}

export function MiniAire({ valeurs, couleur = "#fff", largeur = 220, hauteur = 56 }) {
  if (valeurs.length < 2) return <svg width="100%" height={hauteur} />;
  const max = Math.max(...valeurs, 1);
  const pas = largeur / (valeurs.length - 1);
  const pts = valeurs.map((v, i) => [i * pas, hauteur - 4 - (nb(v) / max) * (hauteur - 8)]);
  const ligne = cheminLisse(pts);
  return (
    <svg width="100%" height={hauteur} viewBox={`0 0 ${largeur} ${hauteur}`} preserveAspectRatio="none" data-testid="mini-aire">
      <defs>
        <linearGradient id="mini-aire-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={couleur} stopOpacity="0.45" />
          <stop offset="100%" stopColor={couleur} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${ligne} L${largeur},${hauteur} L0,${hauteur} Z`} fill="url(#mini-aire-grad)" />
      <path d={ligne} fill="none" stroke={couleur} strokeWidth="2" />
    </svg>
  );
}

/* ---------- Series hebdomadaires ---------- */
// Series par module (hors referentiel regroupe, en dernier), completees a 0 sur toutes les semaines ;
// les semaines partielles (debut avant la periode ou fin apres) sont exclues de la courbe.
export function seriesHebdo(d) {
  const p = d.period || {};
  const agg = new Map();
  const labels = new Set();
  const semaines = new Set();
  liste(d.weekly).forEach((w) => {
    if (!dateValide(w.week_start)) return;
    const l = libelleModule(w);
    labels.add(l);
    semaines.add(w.week_start);
    const k = `${w.week_start}|${l}`;
    agg.set(k, (agg.get(k) || 0) + nb(w.opens));
  });
  const toutes = [...semaines].sort();
  const partielle = (w) => (!!p.to && ajouterJours(w, 6) > p.to) || (!!p.from && w < p.from);
  let gardees = toutes.filter((w) => !partielle(w));
  if (gardees.length === 0) gardees = toutes; // periode plus courte qu'une semaine : on garde tout
  const exclues = toutes.filter((w) => !gardees.includes(w));
  const total = (l) => gardees.reduce((t, w) => t + (agg.get(`${w}|${l}`) || 0), 0);
  const noms = [...labels].sort((a, b) => (a === HORS) - (b === HORS) || total(b) - total(a) || a.localeCompare(b, "fr"));
  const points = [];
  noms.forEach((l) => gardees.forEach((w) => points.push({ week_start: w, module_label: l, opens: agg.get(`${w}|${l}`) || 0 })));
  return { points, noms, gardees, exclues };
}

/* ---------- Tuiles ---------- */
// Tuile KPI unique pour toute la page (Usage et Tableau de bord) : hauteur fixe, libelle en haut,
// puis grand chiffre (gauche) et visuel (droite) sur une ligne. Cliquable si onClick (etat actif bleu).
const tuile = { ...CARTE, background: "#fff", padding: "8px 14px", height: 112, display: "flex", flexDirection: "column" };
const titreTuile = { fontSize: 14, fontWeight: 500, lineHeight: "20px" }; // couleur principale (blanc sur tuile foncee)
const grandChiffre = { fontSize: 28, fontWeight: 700, lineHeight: "32px" };
const HAUTEUR_VISUEL = 26;
const HAUTEUR_CHALEUR = 210;
const ligneSous = (sombre) => ({ fontSize: 12, color: sombre ? "#9fb1c4" : "#6b7785", lineHeight: "16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" });

// Tuile KPI unique pour toute la page (Usage et Tableau de bord), meme structure :
// titre / grand chiffre + sous-titre (ligne de base) / [sous-titre 2] / visuel pleine largeur en bas.
// Cliquable si onClick (etat actif bleu). Sans visuel, le contenu est centre dans la tuile.
export function Tuile({ id, titre, valeur, sous, sous2, visuel, sombre, couleurValeur, onClick, actif, lien }) {
  const [survol, setSurvol] = useState(false);
  const interactif = !!onClick;
  const style = {
    ...tuile,
    ...(sombre ? { background: NUIT, color: "#fff", border: `1px solid ${NUIT}` } : {}),
    ...(actif ? { borderColor: BLEU, background: "#e6f7ff" } : {}),
    ...(interactif ? { cursor: "pointer" } : {}),
    ...(interactif && survol ? { boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)" } : {}), // meme survol que les cartes antd hoverable
  };
  return (
    <div
      data-testid={id}
      role={interactif ? (lien ? "link" : "button") : undefined}
      aria-pressed={interactif && !lien ? !!actif : undefined}
      onMouseEnter={interactif ? () => setSurvol(true) : undefined}
      onMouseLeave={interactif ? () => setSurvol(false) : undefined}
      tabIndex={interactif ? 0 : undefined}
      onClick={interactif ? onClick : undefined}
      onKeyDown={interactif ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
      style={style}
    >
      <div style={titreTuile}>{titre}</div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: visuel ? "flex-start" : "center", minHeight: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
          <span style={{ ...grandChiffre, color: couleurValeur, whiteSpace: "nowrap", flexShrink: 0 }}>{valeur}</span>
          {sous && (
            <Infobulle texte={typeof sous === "string" ? sous : undefined}>
              <span style={{ ...ligneSous(sombre), minWidth: 0 }}>{sous}</span>
            </Infobulle>
          )}
        </div>
        {sous2 && (
          <Infobulle texte={typeof sous2 === "string" ? sous2 : undefined}>
            <div style={ligneSous(sombre)}>{sous2}</div>
          </Infobulle>
        )}
        {visuel && <div style={{ marginTop: "auto", height: HAUTEUR_VISUEL, display: "flex", alignItems: "flex-end" }}>{visuel}</div>}
      </div>
    </div>
  );
}

const FOND_BARRE = "#eef1f5";

// Barre pleine largeur : segments [{cle, valeur, couleur, texte}] ; infobulle sur chaque segment.
export function BarreTuile({ id, segments, total }) {
  return (
    <div data-testid={id} style={{ display: "flex", width: "100%", height: 10, borderRadius: 5, overflow: "hidden", background: FOND_BARRE }}>
      {segments
        .filter((sg) => nb(sg.valeur) > 0)
        .map((sg) => (
          <Infobulle key={sg.cle} texte={sg.texte}>
            <div data-testid={`${id}-${sg.cle}`} style={{ width: `${total > 0 ? (nb(sg.valeur) / total) * 100 : 0}%`, background: sg.couleur }} />
          </Infobulle>
        ))}
    </div>
  );
}

const joursInactif = (e) =>
  e.days_since === null || e.days_since === undefined ? "—" : `${formaterNombre(e.days_since)} j`;
const texteInactif = (e) => `${joursInactif(e)} · ${e.hostname} · ${e.environnement_code}`;
// Pastille courte : le code d'environnement reste lisible, le serveur passe dans l'infobulle
const textePastilleInactif = (e) => `${e.environnement_code} · ${joursInactif(e)}`;

// Pastilles orange compactes (3 max, puis « + N ») ; infobulle avec la liste complete.
export function PastillesInactifs({ liste: l }) {
  // 2 pastilles + « + N » tiennent sans troncature dans un quart de largeur
  const MAX = 2;
  const pastille = { background: ORANGE, color: "#fff", borderRadius: 10, padding: "0 8px", fontSize: 11, lineHeight: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 };
  return (
    <Tooltip
      title={
        <div data-testid="infobulle-inactifs">
          {l.map((e) => (
            <div key={`${e.hostname}||${e.environnement_code}`}>{texteInactif(e)}</div>
          ))}
        </div>
      }
    >
      <div data-testid="pastilles-inactifs" style={{ display: "flex", gap: 4, width: "100%", minWidth: 0 }}>
        {l.slice(0, MAX).map((e) => (
          <span key={`${e.hostname}||${e.environnement_code}`} data-testid="pastille-inactif" style={pastille}>
            {textePastilleInactif(e)}
          </span>
        ))}
        {l.length > MAX && (
          <span data-testid="pastille-reste" style={{ ...pastille, flexShrink: 0 }}>
            + {l.length - MAX}
          </span>
        )}
      </div>
    </Tooltip>
  );
}

export function Tuiles({ d, module }) {
  const history = useHistory();
  const t = d.totals || {};
  const ev = evolution(t.opens, t.previous_opens);
  const semaines = useMemo(() => {
    const m = new Map();
    seriesHebdo(d).points.forEach((w) => m.set(w.week_start, (m.get(w.week_start) || 0) + nb(w.opens)));
    return [...m.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0]))).map((e) => e[1]);
  }, [d]);
  const listeInactifs = [...liste(d.inactive_environments)].sort((a, b) => nb(b.days_since) - nb(a.days_since));
  const inactifs = listeInactifs.length;
  const connus = nb(t.environments_known);
  const actifs = nb(t.environments_active);
  const sansOuverture = Math.max(0, connus - actifs - inactifs);
  const couv = pct(t.views_used, t.views_total);
  return (
    <Rangee>
      <Col xs={24} sm={12} lg={6}>
        <Tuile
          id="kpi-ouvertures"
          sombre
          titre={LIB.ouvertures}
          valeur={formaterNombre(t.opens)}
          sous={
            ev === null ? (
              "—"
            ) : (
              <span style={{ color: ev >= 0 ? VERT : ROUGE }}>
                {ev >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {formaterEvolution(ev)}
              </span>
            )
          }
          // Référence explicite de l'évolution : même durée, juste avant la période choisie
          sous2={
            d.previous_period
              ? `vs ${formaterNombre(t.previous_opens)} du ${formaterJour(d.previous_period.from)} au ${formaterJour(d.previous_period.to)}`
              : undefined
          }
          visuel={<MiniAire valeurs={semaines} largeur={220} hauteur={HAUTEUR_VISUEL} />}
        />
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Tuile
          id="kpi-couverture"
          lien
          onClick={() => history && history.push(urlReferentiel(module))}
          titre="Couverture du référentiel"
          valeur={fmtPct(couv)}
          sous={`${formaterNombre(t.views_used)} / ${formaterNombre(t.views_total)} vues`}
          visuel={<BarreTuile id="barre-couverture" total={nb(t.views_total)} segments={[{ cle: "utilisees", valeur: t.views_used, couleur: BLEU, texte: `${formaterNombre(t.views_used)} vues utilisées` }]} />}
        />
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Tuile
          id="kpi-envs"
          titre={LIB.environnements}
          valeur={fmtPct(pct(actifs, connus))}
          sous={`${formaterNombre(actifs)} / ${formaterNombre(connus)} actifs`}
          visuel={
            <BarreTuile
              id="barre-envs"
              total={connus}
              segments={[
                { cle: "actifs", valeur: actifs, couleur: VERT, texte: `${formaterNombre(actifs)} actifs sur la période` },
                { cle: "inactifs", valeur: inactifs, couleur: ORANGE, texte: `${formaterNombre(inactifs)} inactifs depuis 30 j` },
                { cle: "reste", valeur: sansOuverture, couleur: GRIS, texte: `${formaterNombre(sansOuverture)} sans ouverture sur la période` },
              ]}
            />
          }
        />
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Tuile
          id="kpi-inactifs"
          titre="Inactifs > 30 j"
          valeur={formaterNombre(inactifs)}
          couleurValeur={inactifs > 0 ? ORANGE : VERT}
          sous={inactifs > 0 ? "environnements sans ouverture" : "aucun"}
          visuel={inactifs > 0 ? <PastillesInactifs liste={listeInactifs} /> : null}
        />
      </Col>
    </Rangee>
  );
}

/* ---------- Evolution par module + historique ---------- */
export function Historique({ d }) {
  const h = liste(d.history);
  const p = d.period || {};
  if (h.length === 0) return null;
  const max = Math.max(...h.map((w) => nb(w.opens)), 1);
  const dans = (w) => dateValide(w.week_start) && !!p.from && !!p.to && ajouterJours(w.week_start, 6) >= p.from && w.week_start <= p.to;
  return (
    <div data-testid="historique" style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 40, marginTop: 8 }}>
      {h.map((w) => (
        <div
          key={w.week_start}
          data-testid="histo-barre"
          data-actif={dans(w) ? "1" : "0"}
          title={`${formaterDate(w.week_start)} : ${formaterNombre(w.opens)}`}
          style={{ flex: 1, height: `${Math.max(6, (nb(w.opens) / max) * 100)}%`, borderRadius: 2, background: dans(w) ? JAUNE : GRIS }}
        />
      ))}
    </div>
  );
}

function EvolutionModules({ d }) {
  const { points, noms, exclues } = useMemo(() => seriesHebdo(d), [d]);
  const palette = [BLEU, ORANGE, VERT, JAUNE, ROUGE, NUIT, GRIS];
  const couleurs = noms.map((n, i) => (n === HORS ? GRIS_HORS : palette[i % palette.length]));
  return (
    <Card size="small" title="Évolution par module" data-testid="bloc-hebdo" style={CARTE} headStyle={{ fontSize: 15, fontWeight: 500 }}>
      {points.length === 0 ? (
        vide
      ) : (
        <Area
          data={points.map((w) => ({ ...w, semaine: formaterDate(w.week_start) }))}
          xField="semaine"
          yField="opens"
          seriesField="module_label"
          smooth
          xAxis={{ range: [0, 1] }}
          appendPadding={[4, 0, 0, 0]}
          legend={{ position: "top", marker: { symbol: "circle" } }}
          areaStyle={{ fillOpacity: 0.12 }}
          color={couleurs}
          height={230}
        />
      )}
      {exclues.length > 0 && (
        <div data-testid="semaine-partielle" style={{ fontSize: 11, color: "#6b7785", marginTop: 4 }}>
          Semaine{exclues.length > 1 ? "s" : ""} partielle{exclues.length > 1 ? "s" : ""} non affichée{exclues.length > 1 ? "s" : ""} (semaine en cours) :{" "}
          {exclues.map((w) => formaterDate(w)).join(", ")}
        </div>
      )}
      <Historique d={d} />
    </Card>
  );
}

/* ---------- Cartes a en-tete ---------- */
function Couverture({ d }) {
  // Même ordre que l'Exploration : vues utilisées décroissantes, puis taille du module
  const tries = [...liste(d.coverage)].sort(
    (a, b) => nb(b.views_used) - nb(a.views_used) || nb(b.views_total) - nb(a.views_total)
  );
  const lignes = tries.map((c) => {
    const p = pct(c.views_used, c.views_total);
    return {
      cle: c.module,
      titre: libelleModule(c),
      texte: `${formaterNombre(c.views_used)} / ${formaterNombre(c.views_total)}`,
      valeur: p,
      max: 100,
      couleur: p < 5 ? ORANGE : BLEU,
    };
  });
  return (
    <Bloc id="bloc-couverture" titre="Couverture par module">
      <ListeBarres id="barres-couverture" lignes={lignes} />
    </Bloc>
  );
}

function Tendances({ d }) {
  const t = d.trends || {};
  const lignes = [
    ...liste(t.rising).slice(0, 5).map((r) => ({ ...r, delta: Math.abs(nb(r.delta)) })),
    ...liste(t.falling).slice(0, 5).map((r) => ({ ...r, delta: -Math.abs(nb(r.delta)) })),
  ];
  return (
    <Bloc id="bloc-tendances" titre="Tendances">
      {lignes.length === 0 ? (
        vide
      ) : (
        <div data-testid="pastilles-tendances">
          {lignes.map((l) => (
            <Infobulle key={`${l.delta >= 0}-${l.code}`} texte={nomVue(l)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: NUIT, color: "#fff", borderRadius: 10, padding: "4px 12px", marginBottom: 6 }}>
                <span style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nomVue(l)}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: l.delta >= 0 ? VERT : ROUGE }}>{formaterDelta(l.delta)}</span>
              </div>
            </Infobulle>
          ))}
        </div>
      )}
    </Bloc>
  );
}

function Chaleur({ d }) {
  const cartes = liste(d.heatmap);
  const donnees = useMemo(() => {
    if (cartes.length === 0) return [];
    const heures = cartes.map((c) => nb(c.hour));
    const h0 = Math.max(0, Math.min(...heures) - 1);
    const h1 = Math.min(23, Math.max(...heures) + 1);
    const m = new Map(cartes.map((c) => [`${c.dow}-${c.hour}`, nb(c.opens)]));
    const cases = [];
    JOURS.forEach((j, i) => {
      for (let h = h0; h <= h1; h++) cases.push({ jour: j, hour: `${h} h`, opens: m.get(`${i + 1}-${h}`) || 0 });
    });
    return cases;
  }, [cartes]);
  return (
    <Bloc id="bloc-chaleur" titre="Heures d'activité">
      {donnees.length === 0 ? (
        vide
      ) : (
        <div data-testid="chaleur-corps" style={{ height: HAUTEUR_CHALEUR }}>
          {/* Hauteur explicite : mesurée avant la mise en page, la carte prendrait sa hauteur par défaut (400 px) et serait coupée.
              Cases rectangulaires : elles occupent la largeur ET la hauteur (des carrés débordent en largeur 2/3). */}
          <Heatmap data={donnees} xField="hour" yField="jour" colorField="opens" color={["#eaf2fc", BLEU]} heatmapStyle={{ radius: 3, lineWidth: 2, stroke: "#fff" }} height={HAUTEUR_CHALEUR} autoFit />
        </div>
      )}
    </Bloc>
  );
}

function Lentes({ d }) {
  const lignes = [...liste(d.slowest)]
    .sort((a, b) => nb(b.p95_duration_ms) - nb(a.p95_duration_ms))
    .slice(0, 10)
    .map((v) => {
      const p = nb(v.p95_duration_ms);
      return { cle: v.code, titre: nomVue(v), texte: formaterDureeMs(v.p95_duration_ms), valeur: p, couleur: p > 5000 ? ROUGE : p > 3000 ? ORANGE : BLEU };
    });
  return (
    <Bloc
      id="bloc-lentes"
      titre="Vues les plus lentes"
      extra={
        <Tooltip title="Temps sous lequel se font 95 % des ouvertures">
          <span data-testid="aide-p95" style={{ color: "#fff", fontSize: 12 }}>
            {LIB.p95} <InfoCircleOutlined />
          </span>
        </Tooltip>
      }
    >
      <ListeBarres id="barres-lentes" lignes={lignes} />
    </Bloc>
  );
}

function Adoption({ d }) {
  const total = nb((d.adoption || {}).environments_total);
  const lignes = liste((d.adoption || {}).views)
    .slice(0, 10)
    .map((v) => ({ cle: v.code, titre: nomVue(v), texte: `${formaterNombre(v.environments)} / ${formaterNombre(total)} env.`, valeur: nb(v.environments), max: total }));
  return (
    <Bloc
      id="bloc-adoption"
      titre="Vues les plus répandues"
      extra={
        <Tooltip title="Nombre d'environnements qui ont ouvert la vue sur la période, sur le total des environnements actifs">
          <span data-testid="aide-repandues" style={{ color: "#fff" }}>
            <InfoCircleOutlined />
          </span>
        </Tooltip>
      }
    >
      <ListeBarres id="barres-adoption" lignes={lignes} />
    </Bloc>
  );
}

function Favoris({ d }) {
  const lignes = liste(d.favorites)
    .slice(0, 10)
    .map((v) => ({ cle: v.code, titre: nomVue(v), texte: formaterNombre(v.favorite_opens), valeur: nb(v.favorite_opens) }));
  return (
    <Bloc id="bloc-favoris" titre="Favoris">
      <ListeBarres id="barres-favoris" lignes={lignes} />
    </Bloc>
  );
}

// Piles de cartes independantes (hauteur au contenu, pas de rangees alignees) ; 3 colonnes, puis 2 (< 1200 px), puis 1.
// Rangees alignees (CSS grid, cartes de meme hauteur par rangee, gouttiere 12 px identique a celle des tuiles).
// < 1200 px : 2 colonnes ; < 768 px : 1 colonne, meme ordre.
export const CSS_GRILLE = `
.usage-grille { display: grid; gap: 12px; align-items: stretch; margin-bottom: 12px; }
.usage-grille > * { min-width: 0; }
.usage-grille-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.usage-grille-1 { grid-template-columns: minmax(0, 1fr); }
.usage-grille-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
@media (max-width: 1199px) { .usage-grille-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 767px) { .usage-grille-3, .usage-grille-2 { grid-template-columns: minmax(0, 1fr); } }
`;

function TableauBord({ d, module }) {
  return (
    <>
      <style>{CSS_GRILLE}</style>
      <Tuiles d={d} module={module} />
      <Rangee>
        <Col span={24}>
          <EvolutionModules d={d} />
        </Col>
      </Rangee>
      <div data-testid="rangee-1" className="usage-grille usage-grille-3">
        <Couverture d={d} />
        <Tendances d={d} />
        <Lentes d={d} />
      </div>
      <div data-testid="rangee-2" className="usage-grille usage-grille-1">
        <Chaleur d={d} />
      </div>
      <div data-testid="rangee-3" className="usage-grille usage-grille-2">
        <Adoption d={d} />
        <Favoris d={d} />
      </div>
    </>
  );
}

/* ---------- Exploration ---------- */
const PAS_MODULE = 52;
const PAS_VUE = 58;
const MAX_VUES = 12;
const VB = 600;

// Modules ayant des ouvertures, tries par ouvertures ; chaque module porte ses vues triees.
export function modulesExploration(d) {
  const utilisees = new Map(liste(d.coverage).map((c) => [c.module, nb(c.views_used)]));
  const m = new Map();
  liste(d.views)
    .filter((v) => nb(v.opens) > 0)
    .forEach((v) => {
      const k = cleModule(v);
      if (!m.has(k)) m.set(k, { code: k, label: libelleModule(v), vues: [], opens: 0 });
      const g = m.get(k);
      g.vues.push(v);
      g.opens += nb(v.opens);
    });
  return [...m.values()]
    .map((g) => ({ ...g, vues: g.vues.sort((a, b) => nb(b.opens) - nb(a.opens)), utilisees: utilisees.has(g.code) ? utilisees.get(g.code) : g.vues.length }))
    // Modules triés par nombre de vues utilisées, puis par ouvertures ; Hors référentiel en dernier
    .sort((a, b) => (a.code === CLE_HORS) - (b.code === CLE_HORS) || b.utilisees - a.utilisees || b.opens - a.opens);
}

const lien = (cle, x1, y1, x2, y2, actif) => {
  const mx = (x1 + x2) / 2;
  return <path key={cle} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none" stroke={actif ? JAUNE : "#2f4d6b"} strokeWidth={actif ? 2 : 1.5} vectorEffect="non-scaling-stroke" />;
};

function Fiche({ vue, moduleLabel, d }) {
  if (!vue) return <div style={{ opacity: 0.7 }}>Sélectionner une vue</div>;
  const ev = evolution(vue.opens, vue.previous_opens);
  const fav = liste(d.favorites).find((f) => f.code === vue.code);
  const actifs = nb((d.totals || {}).environments_active);
  const p95 = vue.p95_duration_ms;
  // Courbe hebdomadaire de la vue : uniquement si l'API fournit views[].weekly (non derivable de weekly, grain module).
  const courbe = liste(vue.weekly)
    .filter((w) => dateValide(w.week_start))
    .sort((a, b) => String(a.week_start).localeCompare(String(b.week_start)))
    .map((w) => nb(w.opens));
  const mini = { background: "#173a5c", borderRadius: 10, padding: "8px 12px" };
  return (
    <div data-testid="fiche-vue">
      <div style={{ fontSize: 12, opacity: 0.7 }}>{moduleLabel}</div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{vue.code}</div>
      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 12 }}>{vue.label || "sans libellé"}</div>
      <div style={{ fontSize: 44, fontWeight: 700, lineHeight: 1 }}>{formaterNombre(vue.opens)}</div>
      <div style={{ fontSize: 14, marginBottom: 14, color: ev === null ? "#9fb1c4" : ev >= 0 ? VERT : ROUGE }}>
        {ev === null ? "—" : <>{ev >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {formaterEvolution(ev)}</>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={mini}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{LIB.environnements}</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {vue.environments === null || vue.environments === undefined ? "—" : `${formaterNombre(vue.environments)} / ${formaterNombre(actifs)}`}
          </div>
        </div>
        <div style={mini}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{LIB.p95}</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: nb(p95) > 5000 ? ROUGE : undefined }}>{formaterDureeMs(p95)}</div>
        </div>
        <div style={mini}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{LIB.precedente}</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{formaterNombre(vue.previous_opens)}</div>
        </div>
        {fav && (
          <div style={mini}>
            <div style={{ fontSize: 11, opacity: 0.7 }}>Favoris</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{formaterNombre(fav.favorites)}</div>
          </div>
        )}
      </div>
      {courbe.length > 1 && (
        <div data-testid="fiche-courbe" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Ouvertures par semaine</div>
          <MiniAire valeurs={courbe} couleur={ev !== null && ev < 0 ? ROUGE : VERT} largeur={300} hauteur={56} />
        </div>
      )}
    </div>
  );
}

export function Exploration({ d, params }) {
  const modules = useMemo(() => modulesExploration(d), [d]);
  const [choixModule, setChoixModule] = useState(null);
  const [choixVue, setChoixVue] = useState(null);
  const module = modules.find((m) => m.code === choixModule) || modules[0];
  const top = module ? module.vues.slice(0, MAX_VUES) : [];
  const reste = module ? module.vues.length - top.length : 0;
  const vue = (module && module.vues.find((v) => v.code === choixVue)) || top[0] || null;
  const racine = params && params.hostname ? `${params.hostname} · ${params.environnement_code}` : "Tous les environnements";

  const nVues = top.length + (reste > 0 ? 1 : 0);
  const H = Math.max(220, modules.length * PAS_MODULE + 16, nVues * PAS_VUE + 16);
  const y0m = (H - modules.length * PAS_MODULE) / 2;
  const y0v = (H - nVues * PAS_VUE) / 2;
  const yRacine = H / 2;
  const iMod = module ? modules.indexOf(module) : -1;
  const maxVue = Math.max(...top.map((v) => nb(v.opens)), 1);
  const pctX = (p) => (p / 100) * VB;

  return (
    <div data-testid="exploration" style={{ background: NUIT, color: "#e8eef5", borderRadius: 12, padding: 16 }}>
      {modules.length === 0 ? (
        <div style={{ color: "#9fb1c4", padding: 24 }}>Aucune ouverture sur la période</div>
      ) : (
        <Row gutter={16}>
          <Col xs={24} lg={14}>
            <div data-testid="legende-vues" style={{ marginLeft: "72%", fontSize: 11, display: "flex", gap: 10, marginBottom: 4 }}>
              {[
                ["baisse", ROUGE, `≤ −${SEUIL_TENDANCE} %`],
                ["stable", BLEU, ""],
                ["hausse", VERT, `≥ +${SEUIL_TENDANCE} %`],
              ].map(([nom, c, seuil]) => (
                <span key={nom} title={seuil || undefined}>
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: c, marginRight: 4 }} />
                  {nom}
                </span>
              ))}
            </div>
            <div style={{ position: "relative", height: H }}>
              <svg width="100%" height={H} viewBox={`0 0 ${VB} ${H}`} preserveAspectRatio="none" style={{ position: "absolute", left: 0, top: 0 }}>
                {modules.map((m, i) => lien(`m${m.code}`, pctX(22), yRacine, pctX(33), y0m + i * PAS_MODULE + PAS_MODULE / 2 - 4, m === module))}
                {top.map((v, i) => lien(`v${v.code}`, pctX(63), y0m + iMod * PAS_MODULE + PAS_MODULE / 2 - 4, pctX(72), y0v + i * PAS_VUE + PAS_VUE / 2 - 4, true))}
              </svg>
              <div
                data-testid="noeud-racine"
                style={{ position: "absolute", left: 0, width: "22%", top: yRacine - 24, minHeight: 48, background: "#173a5c", borderRadius: 10, padding: "6px 8px", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center" }}
              >
                {racine}
              </div>
              {modules.map((m, i) => (
                <div
                  key={m.code}
                  data-testid={`noeud-module-${m.code}`}
                  data-actif={m === module ? "1" : "0"}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setChoixModule(m.code);
                    setChoixVue(null);
                  }}
                  style={{ position: "absolute", left: "33%", width: "30%", top: y0m + i * PAS_MODULE, height: PAS_MODULE - 8, background: "#173a5c", border: `2px solid ${m === module ? JAUNE : "transparent"}`, borderRadius: 10, padding: "4px 10px", cursor: "pointer", fontSize: 12, display: "flex", flexDirection: "column", justifyContent: "center" }}
                >
                  <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: m.code === CLE_HORS ? GRIS_HORS : undefined }}>
                    {m.label} · {vues(m.utilisees)}
                  </strong>
                  <span style={{ opacity: 0.7 }}>{formaterNombre(m.opens)} ouvertures</span>
                </div>
              ))}
              {top.map((v, i) => (
                <div
                  key={v.code}
                  data-testid={`noeud-vue-${v.code}`}
                  data-tendance={tendance(v)}
                  role="button"
                  tabIndex={0}
                  onClick={() => setChoixVue(v.code)}
                  style={{ position: "absolute", left: "72%", width: "28%", top: y0v + i * PAS_VUE, height: PAS_VUE - 8, cursor: "pointer", fontSize: 11, padding: "2px 6px", borderRadius: 8, border: `1px solid ${vue && vue.code === v.code ? "#fff" : "transparent"}` }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 4 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.code}</span>
                    <span>{formaterNombre(v.opens)}</span>
                  </div>
                  <Infobulle texte={nomVue(v)}>
                    <div data-testid={`libelle-vue-${v.code}`} style={{ fontSize: 10, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.label || "sans libellé"}
                    </div>
                  </Infobulle>
                  <div style={{ height: 6, borderRadius: 3, background: COULEUR_TENDANCE[tendance(v)], width: `${Math.max(4, (nb(v.opens) / maxVue) * 100)}%` }} />
                </div>
              ))}
              {reste > 0 && (
                <div data-testid="noeud-reste" style={{ position: "absolute", left: "72%", width: "28%", top: y0v + top.length * PAS_VUE, fontSize: 11, opacity: 0.7, padding: "2px 6px" }}>
                  + {reste} {reste > 1 ? "vues" : "vue"}
                </div>
              )}
            </div>
          </Col>
          <Col xs={24} lg={10}>
            <div style={{ borderLeft: "1px solid #2f4d6b", paddingLeft: 16, minHeight: 200 }}>
              <Fiche vue={vue} moduleLabel={module ? module.label : ""} d={d} />
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default function UsageStats({ params, filtres, layout }) {
  const [etat, setEtat] = useState({ chargement: true, erreur: false, data: null });
  const cle = params ? JSON.stringify(params) : null;

  useEffect(() => {
    if (!params) return undefined;
    let actif = true;
    setEtat((e) => ({ ...e, chargement: true, erreur: false }));
    axios
      .get("/api/usage/stats", { params })
      .then((res) => actif && setEtat({ chargement: false, erreur: false, data: (res && res.data) || {} }))
      .catch(() => actif && setEtat({ chargement: false, erreur: true, data: null }));
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle]);

  const d = etat.data || {};
  return (
    <div className="usage-stats">
      {filtres}
      {etat.erreur && <Alert type="error" showIcon message="Impossible de charger les statistiques." style={{ marginBottom: 16 }} />}
      {etat.chargement && !etat.data ? (
        <Spin tip="Chargement…" />
      ) : etat.data ? (
        <Spin spinning={etat.chargement}>{layout === "explore" ? <Exploration d={d} params={params} /> : <TableauBord d={d} module={params && params.module} />}</Spin>
      ) : null}
    </div>
  );
}
